import type { ApiClient, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createEditorState } from '@/features/content/editor/editorNormalizer';
import { serializeEditorState } from '@/features/content/editor/editorSerializer';
import type {
  EditableContentUnit,
  RawComponent,
  RawContentSection,
  RawSection,
  RawUpdateCoursePayload,
  UpdateCourseSavePayload,
} from '@/features/content/editor/editorTypes';

import {
  getSectionUnits,
  groupUnitsIntoSections,
} from './groupUnitsIntoSections';
import type {
  ContentSection,
  CopySectionPosition,
} from './sectionTypes';

interface CopySectionParams {
  courseId: string;
  idFactory?: () => string;
  position: CopySectionPosition;
  sections: ContentSection[];
  targetComponent: RawComponent;
}

interface UnitOwnership {
  sourceContentSectionId?: string;
  sourceContentSectionIndex: number;
  sourceSectionId: string;
  sourceSectionIndex: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function getContentSection(section: RawSection): RawContentSection | null {
  const contentSection = section.content_section ?? section.contentSection;
  return isRecord(contentSection) ? (contentSection as RawContentSection) : null;
}

function getSectionId(section: RawSection, sectionIndex: number) {
  return (
    normalizeIdentifier(section.id) ??
    normalizeIdentifier(section.uid) ??
    `__generated-section-${sectionIndex + 1}`
  );
}

function getContentSectionId(
  contentSection: RawContentSection | null,
  sectionIndex: number,
) {
  return (
    normalizeIdentifier(contentSection?.id) ??
    normalizeIdentifier(contentSection?.uid) ??
    `__generated-content-section-${sectionIndex + 1}`
  );
}

function defaultIdFactory() {
  if (globalThis.crypto && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function unwrapSavedComponent(
  response:
    | RawUpdateCoursePayload
    | LegacyKuduResponseEnvelope<RawUpdateCoursePayload>
    | RawComponent
    | null
    | undefined,
  fallbackComponent: RawComponent,
): RawComponent {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapSavedComponent(
      response.Response as RawUpdateCoursePayload | RawComponent,
      fallbackComponent,
    );
  }

  if (
    response &&
    typeof response === 'object' &&
    'components' in response &&
    Array.isArray(response.components)
  ) {
    const [firstComponent] = response.components;
    return (firstComponent as RawComponent | undefined) ?? fallbackComponent;
  }

  if (response && typeof response === 'object' && 'component' in response) {
    return (response.component as RawComponent | null | undefined) ?? fallbackComponent;
  }

  if (
    response &&
    typeof response === 'object' &&
    ('sections' in response || 'id' in response || 'uid' in response)
  ) {
    return response as RawComponent;
  }

  return fallbackComponent;
}

function getInsertionIndex(
  units: EditableContentUnit[],
  position: CopySectionPosition,
) {
  if (position.placement === 'first') {
    return 0;
  }

  const grouping = groupUnitsIntoSections(units);
  const targetSection = grouping.sections.find(
    (section) => section.id === position.sectionId,
  );

  if (!targetSection) {
    return units.length;
  }

  if (position.placement === 'before') {
    return targetSection.headerUnitIndex;
  }

  const lastBodyIndex =
    targetSection.bodyUnitIndices[targetSection.bodyUnitIndices.length - 1];

  return lastBodyIndex === undefined
    ? targetSection.headerUnitIndex + 1
    : lastBodyIndex + 1;
}

function getFallbackOwnership(targetComponent: RawComponent): UnitOwnership {
  const sections = Array.isArray(targetComponent.sections)
    ? (targetComponent.sections.filter(isRecord) as RawSection[])
    : [];

  const contentSectionIndex = sections.findIndex((section) =>
    Boolean(getContentSection(section)),
  );
  const sectionIndex = contentSectionIndex >= 0 ? contentSectionIndex : 0;
  const section = sections[sectionIndex];

  if (!section) {
    throw new Error('Cannot copy a section into a component with no sections.');
  }

  const contentSection = getContentSection(section);

  return {
    sourceContentSectionId: getContentSectionId(contentSection, sectionIndex),
    sourceContentSectionIndex: sectionIndex,
    sourceSectionId: getSectionId(section, sectionIndex),
    sourceSectionIndex: sectionIndex,
  };
}

function getInsertionOwnership(
  targetComponent: RawComponent,
  targetUnits: EditableContentUnit[],
  insertionIndex: number,
): UnitOwnership {
  const nearbyUnit = targetUnits[insertionIndex - 1] ?? targetUnits[insertionIndex];

  if (nearbyUnit) {
    return {
      sourceContentSectionId: nearbyUnit.sourceContentSectionId,
      sourceContentSectionIndex: nearbyUnit.sourceContentSectionIndex,
      sourceSectionId: nearbyUnit.sourceSectionId,
      sourceSectionIndex: nearbyUnit.sourceSectionIndex,
    };
  }

  return getFallbackOwnership(targetComponent);
}

function createCopiedUnits(
  sections: ContentSection[],
  ownership: UnitOwnership,
  idFactory: () => string,
) {
  return sections.flatMap((section) =>
    getSectionUnits(section).map((unit) => ({
      ...unit,
      ...ownership,
      id: `${unit.id}-copy-${idFactory()}`,
      isNew: false,
    })),
  );
}

export function createComponentWithCopiedSections({
  idFactory = defaultIdFactory,
  position,
  sections,
  targetComponent,
}: Omit<CopySectionParams, 'courseId'>): ReturnType<typeof createEditorState> {
  const targetState = createEditorState(targetComponent);
  const insertionIndex = getInsertionIndex(targetState.contentUnits, position);
  const ownership = getInsertionOwnership(
    targetComponent,
    targetState.contentUnits,
    insertionIndex,
  );
  const copiedUnits = createCopiedUnits(sections, ownership, idFactory);
  const contentUnits = [...targetState.contentUnits];

  contentUnits.splice(insertionIndex, 0, ...copiedUnits);

  return {
    ...targetState,
    contentUnits,
    isDirty: true,
  };
}

export async function copySections(
  client: ApiClient,
  {
    courseId,
    idFactory,
    position,
    sections,
    targetComponent,
  }: CopySectionParams,
): Promise<RawComponent> {
  if (sections.length === 0) {
    throw new Error('Select at least one section to copy.');
  }

  const nextEditorState = createComponentWithCopiedSections({
    idFactory,
    position,
    sections,
    targetComponent,
  });
  const payload: UpdateCourseSavePayload = serializeEditorState(
    nextEditorState,
    courseId,
  );
  const [fallbackComponent] = payload.components;

  if (!fallbackComponent) {
    throw new Error('Unable to construct the copied component payload.');
  }

  const response = await client.invokeKuduMethod<
    RawUpdateCoursePayload | LegacyKuduResponseEnvelope<RawUpdateCoursePayload>,
    UpdateCourseSavePayload
  >('UpdateCourse', payload);

  return unwrapSavedComponent(response, fallbackComponent);
}
